import { StudioIntegrationTemplate } from '@goldcast/api/backend';

import { SolutionInstance, State, FieldMappingBooleans } from './types';

import { TRAY_APPS_NAME } from '@/constants';
import { imageSrcPath } from '@/utils';

export default {
  getAuthDetailsByIds: state => authIds => {
    return (state.trayAuthentications ?? [])
      .filter(auth => authIds.includes(auth.id))
      .map(auth => ({
        name: auth.name,
        authId: auth.id,
        icon: auth.service.icon,
        authTrayTitle: auth.service.name
      }));
  },
  getConnectedSolutionInstances: (state, _, __, rootGetters): SolutionInstance[] => {
    let enabledInstances = (state.traySolutionInstances || []).filter(instance => instance.enabled);

    const currentOrg = rootGetters['org/getCurrentOrg'];
    const currentEvent = rootGetters['studioEventList/getCurrentEvent'];
    const eloquaFormID = rootGetters['org/eloquaConnectedEventFormID'];
    const driftScript = currentOrg?.drift_script;
    const isFormMapping = rootGetters['studioForms/getIsFormMapping'];
    // Get workspace ID from either form mapping or event mapping
    const assignedWorkspaceId = isFormMapping
      ? rootGetters['studioForms/getCurrentForm']?.workspace_list?.[0]?.id
      : currentEvent?.workspace?.id;
    // Filter instances based on workspace assignment
    enabledInstances = enabledInstances.filter(instance =>
      assignedWorkspaceId ? instance.teams?.includes(assignedWorkspaceId) : instance.default
    );

    const connectedApps: SolutionInstance[] = [];

    if (driftScript) {
      connectedApps.push({
        id: 'drift',
        name: 'Drift',
        icon: imageSrcPath('assets/drift-new.png'),
        type: 'drift',
        solutionTitle: 'Drift',
        solutionId: 'drift',
        enabled: true
      });
    }

    enabledInstances.forEach(instance => {
      const isHubspotConnected = instance.solutionTitle.includes(TRAY_APPS_NAME['hubspot']);
      if (isHubspotConnected) {
        connectedApps.push({
          ...instance,
          type: 'hubspot',
          enabledForEvent: Boolean(currentEvent && currentEvent.hubspot_status)
        });
        return;
      }

      const isMarketoConnected = instance.solutionTitle.includes(TRAY_APPS_NAME['marketo']);
      if (isMarketoConnected) {
        connectedApps.push({
          ...instance,
          type: 'marketo',
          enabledForEvent: Boolean(currentEvent && currentEvent.marketo_status)
        });
        return;
      }

      const isPardotConnected = instance.solutionTitle.includes(TRAY_APPS_NAME['pardot']);
      if (isPardotConnected) {
        connectedApps.push({
          ...instance,
          type: 'pardot',
          enabledForEvent: Boolean(currentEvent && currentEvent.pardot_status)
        });
        return;
      }

      // order of if conditions matter here - don't mess up
      const isSlackPlusSalesforceConnected = instance.solutionTitle.includes(TRAY_APPS_NAME['slackPlusSalesforce']);
      if (isSlackPlusSalesforceConnected) {
        connectedApps.push({
          ...instance,
          type: 'slack-plus-salesforce',
          enabledForEvent: Boolean(currentEvent && currentEvent.slack_plus_salesforce_channel_id)
        });
        return;
      }

      const isSalesforceConnected = instance.solutionTitle.includes(TRAY_APPS_NAME['salesforce']);
      if (isSalesforceConnected) {
        connectedApps.push({
          ...instance,
          type: 'salesforce',
          enabledForEvent: Boolean(currentEvent && currentEvent.salesforce_status)
        });
        return;
      }

      const isSlackConnected = instance.solutionTitle.includes(TRAY_APPS_NAME['slack']);
      if (isSlackConnected) {
        connectedApps.push({
          ...instance,
          type: 'slack',
          enabledForEvent: Boolean(currentEvent && currentEvent.slack_channel_id)
        });
        return;
      }

      const isWistiaConnected = instance.solutionTitle.includes(TRAY_APPS_NAME['wistia']);
      if (isWistiaConnected && currentEvent?.host_type !== 'In_Person') {
        connectedApps.push({
          ...instance,
          type: 'wistia',
          enabledForEvent: Boolean(currentEvent && currentEvent.wistia_project_id)
        });
      }

      const isWebhooksConnected = instance.solutionTitle.includes(TRAY_APPS_NAME['webhooks']);
      if (isWebhooksConnected) {
        connectedApps.push({ ...instance, type: 'webhooks' });
        return;
      }

      const isEloquaConnected = instance.solutionTitle.includes(TRAY_APPS_NAME['eloqua']);
      if (Boolean(eloquaFormID) && isEloquaConnected) {
        connectedApps.push({
          ...instance,
          type: 'eloqua',
          enabledForEvent: Boolean(currentEvent && currentEvent.eloqua_status)
        });
        return;
      }
    });

    return connectedApps;
  },
  getConnectedSolutionInstancesForSeries:
    (state, _, __, rootGetters) =>
    (currentSeriesId: string): SolutionInstance[] => {
      let enabledInstances = (state.traySolutionInstances || []).filter(instance => instance.enabled);

      const connectedApps: SolutionInstance[] = [];
      const currentSeries = rootGetters['studioSeriesList/getCurrentSeriesById'](currentSeriesId);
      const assignedWorkspaceId = currentSeries?.workspace?.id;
      enabledInstances = enabledInstances.filter(instance =>
        assignedWorkspaceId ? instance.teams?.includes(assignedWorkspaceId) : instance.default
      );

      enabledInstances.forEach(instance => {
        const isMarketoConnected = instance.solutionTitle.includes(TRAY_APPS_NAME['marketo']);
        if (isMarketoConnected) {
          connectedApps.push({
            ...instance,
            type: 'marketo',
            enabledForSeries: Boolean(currentSeries?.marketo_status)
          });
          return;
        }
      });

      return connectedApps;
    },
  areThereFieldMappingError(state: State): FieldMappingBooleans {
    const areThereFieldMappingError = {};
    state.crmObjectTypes.forEach(({ crm_object_type }) => {
      areThereFieldMappingError[crm_object_type] = state.fieldMappingRowError[crm_object_type].some(
        rowError => rowError !== null
      );
    });
    return areThereFieldMappingError;
  },
  areThereInvalidFieldMapping(state: State): FieldMappingBooleans {
    const areThereInvalidFieldMapping = {};
    state.crmObjectTypes.forEach(({ crm_object_type }) => {
      areThereInvalidFieldMapping[crm_object_type] = state.fieldMappingRowError[crm_object_type].some(
        rowError => rowError && rowError.code === 'MISSING_TARGET_FIELD'
      );
    });
    return areThereInvalidFieldMapping;
  },
  areThereFieldMappingChanges(state: State): FieldMappingBooleans {
    const areThereFieldMappingChanges = {};
    state.crmObjectTypes.forEach(({ crm_object_type }) => {
      if (state.fieldMappings[crm_object_type].mappings.length !== state.prevFieldMappings[crm_object_type].length) {
        areThereFieldMappingChanges[crm_object_type] = true;
        return;
      }
      areThereFieldMappingChanges[crm_object_type] = state.fieldMappings[crm_object_type].mappings.some(
        (fieldMapping, index) =>
          JSON.stringify(fieldMapping) !== JSON.stringify(state.prevFieldMappings[crm_object_type][index])
      );
    });
    return areThereFieldMappingChanges;
  },
  isValidAuthConnected(state: State): boolean {
    const areThereAllInvalidTargetFields = {};
    state.crmObjectTypes.forEach(({ crm_object_type }) => {
      areThereAllInvalidTargetFields[crm_object_type] = state.targetFields[crm_object_type].error !== null;
    });
    return !Object.values(areThereAllInvalidTargetFields).every(isInvalid => isInvalid);
  },
  isFieldMappingApplicable(state: State, _, rootState, rootGetters): boolean {
    const eloquaFormID = rootGetters['org/eloquaConnectedEventFormID'];
    const isValidEloquaFormConnected = rootState?.eventIntegration?.isValidEloquaFormConnected;
    return (
      state.currentCrmType === 'HUBSPOT' ||
      state.currentCrmType === 'MARKETO' ||
      state.currentCrmType === 'SALESFORCE' ||
      state.currentCrmType === 'PARDOT' ||
      (Boolean(eloquaFormID) && state.currentCrmType === 'ELOQUA' && isValidEloquaFormConnected)
    );
  },
  getTemplateConnectedSolutionInstances: (state, _, __, rootGetters): SolutionInstance[] => {
    let enabledInstances = (state.traySolutionInstances || []).filter(instance => instance.enabled);
    const currentOrg = rootGetters['org/getCurrentOrg'];
    const currentIntegrationTemplate: StudioIntegrationTemplate =
      rootGetters['studioTemplateList/getCurrentIntegrationTemplate'];
    const eloquaFormID = rootGetters['org/eloquaConnectedEventFormID'];
    const driftScript = currentOrg?.drift_script;
    const assignedWorkspaceId = rootGetters['studioTemplateList/getCurrentTemplate']?.workspace?.id;
    enabledInstances = enabledInstances.filter(instance =>
      assignedWorkspaceId ? instance.teams?.includes(assignedWorkspaceId) : instance.default
    );

    const connectedApps: SolutionInstance[] = [];
    const addConnectedApp = (instance, type, enabledForTemplate = true) => {
      connectedApps.push({
        ...instance,
        type,
        enabledForTemplate
      });
    };
    if (driftScript) {
      connectedApps.push({
        id: 'drift',
        name: 'Drift',
        icon: imageSrcPath('assets/drift-new.png'),
        type: 'drift',
        solutionTitle: 'Drift',
        solutionId: 'drift',
        enabled: true,
        enabledForTemplate: true
      });
    }
    const appConditions = [
      {
        name: 'hubspot',
        check: instance => instance.solutionTitle.includes(TRAY_APPS_NAME['hubspot']),
        enabled: () => Boolean(currentIntegrationTemplate?.hubspot_settings?.enabled)
      },
      {
        name: 'marketo',
        check: instance => instance.solutionTitle.includes(TRAY_APPS_NAME['marketo']),
        enabled: () => Boolean(currentIntegrationTemplate?.marketo_settings?.enabled)
      },
      {
        name: 'pardot',
        check: instance => instance.solutionTitle.includes(TRAY_APPS_NAME['pardot']),
        enabled: () => Boolean(currentIntegrationTemplate?.pardot_settings?.enabled)
      },
      {
        name: 'slack-plus-salesforce',
        check: instance => instance.solutionTitle.includes(TRAY_APPS_NAME['slackPlusSalesforce']),
        enabled: () => Boolean((currentIntegrationTemplate?.pings as Record<string, any>[])?.length > 0)
      },
      {
        name: 'salesforce',
        check: instance => instance.solutionTitle.includes(TRAY_APPS_NAME['salesforce']),
        enabled: () => Boolean(currentIntegrationTemplate?.salesforce_settings?.enabled)
      },
      {
        name: 'slack',
        check: instance => instance.solutionTitle.includes(TRAY_APPS_NAME['slack']),
        enabled: () => Boolean(currentIntegrationTemplate?.slack_settings?.channel_id)
      },
      {
        name: 'wistia',
        check: instance => instance.solutionTitle.includes(TRAY_APPS_NAME['wistia']),
        enabled: () => Boolean(currentIntegrationTemplate?.wistia_settings?.project_id)
      },
      {
        name: 'webhooks',
        check: instance => instance.solutionTitle.includes(TRAY_APPS_NAME['webhooks']),
        enabled: () => Boolean((currentIntegrationTemplate?.webhooks as Record<string, any>[])?.length > 0)
      },
      {
        name: 'eloqua',
        check: instance => instance.solutionTitle.includes(TRAY_APPS_NAME['eloqua']) && Boolean(eloquaFormID),
        enabled: () => Boolean(currentIntegrationTemplate?.eloqua_settings?.enabled)
      }
    ];
    enabledInstances.forEach(instance => {
      for (const condition of appConditions) {
        if (condition.check(instance)) {
          addConnectedApp(instance, condition.name, condition.enabled());
          break;
        }
      }
    });
    return connectedApps;
  }
};
